import { QueryClient, focusManager } from "@tanstack/react-query";
import { getAuthTokenGetter, reinitializeSession } from "./client";
import { isPanelistNotFoundError, isUnauthorizedError } from "./errors";
import { probeSessionValidity } from "./fetchers";

async function ensureSessionIsValid() {
  const tokenGetter = getAuthTokenGetter();
  const hasToken = await tokenGetter?.();
  try {
    const sessionValidity = await probeSessionValidity();
    if (!sessionValidity.valid && hasToken) {
      await reinitializeSession();
    }
  } catch (error) {
    if (isUnauthorizedError(error) && hasToken) {
      await reinitializeSession();
      return;
    }

    throw error;
  }
}

let focusSessionCheckPromise: Promise<void> | null = null;

focusManager.setEventListener((handleFocus) => {
  const onFocus = () => {
    if (document.visibilityState !== "visible") {
      return;
    }

    if (!focusSessionCheckPromise) {
      focusSessionCheckPromise = ensureSessionIsValid()
        .catch(() => {
          // Let normal query execution surface unrelated probe failures.
        })
        .finally(() => {
          focusSessionCheckPromise = null;
          handleFocus(true);
        });
      return;
    }

    void focusSessionCheckPromise.finally(() => {
      handleFocus(true);
    });
  };

  window.addEventListener("visibilitychange", onFocus, false);
  window.addEventListener("focus", onFocus, false);

  return () => {
    window.removeEventListener("visibilitychange", onFocus);
    window.removeEventListener("focus", onFocus);
  };
});

export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: (failureCount, error) =>
        !isPanelistNotFoundError(error) &&
        !isUnauthorizedError(error) &&
        failureCount < 1,
      refetchOnMount: false,
      refetchOnWindowFocus: true,
      staleTime: 60_000,
    },
  },
});
